import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import ScrollToTop from "@/components/utils/ScrollToTop";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import ListingsPage from "@/pages/ListingsPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import FrameworkPage from "@/pages/FrameworkPage";
import ContactPage from "@/pages/ContactPage";
import AboutMePage from "@/pages/sub/AboutMePage";
import ServicesPage from "@/pages/sub/ServicesPage";
import ProvenResultsPage from "@/pages/sub/ProvenResultsPage";
import ProvenResultDetailPage from "@/pages/sub/ProvenResultDetailPage";
import TestimonialsPage from "@/pages/sub/TestimonialsPage";
import AwardsPage from "@/pages/sub/AwardsPage";
import PropertyValuationPage from "@/pages/sub/PropertyValuationPage";
import CalculatorsPage from "@/pages/sub/CalculatorsPage";
import BlogsPage from "@/pages/sub/BlogsPage";
import BlogDetailPage from "@/pages/sub/BlogDetailPage";
import PrivacyPage from "@/pages/sub/PrivacyPage";
import DisclaimerPage from "@/pages/sub/DisclaimerPage";
// Admin
import AdminLogin from "@/pages/admin/AdminLogin";
import ForgotPassword from "@/pages/admin/ForgotPassword";
import ResetPassword from "@/pages/admin/ResetPassword";
import AdminLayout from "@/components/admin/AdminLayout";
import RequireAdmin from "@/components/admin/RequireAdmin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import LeadsPage from "@/pages/admin/LeadsPage";
import CollectionManager from "@/pages/admin/CollectionManager";
import SeoSettings from "@/pages/admin/SeoSettings";
import TrackingPage from "@/pages/admin/TrackingPage";
import ListingsList from "@/pages/admin/ListingsList";
import ListingForm from "@/pages/admin/ListingForm";
import TestimonialsList from "@/pages/admin/TestimonialsList";
import TestimonialForm from "@/pages/admin/TestimonialForm";
import AwardsList from "@/pages/admin/AwardsList";
import AwardForm from "@/pages/admin/AwardForm";
import BlogList from "@/pages/admin/BlogList";
import BlogForm from "@/pages/admin/BlogForm";
import BlogCategories from "@/pages/admin/BlogCategories";
import ProvenResultsList from "@/pages/admin/ProvenResultsList";
import ProvenResultsForm from "@/pages/admin/ProvenResultsForm";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* ---- Admin (no public chrome) ---- */}
          <Route path="/admin" element={<AdminLogin />} />
          {/* Password recovery — public by design: reached before sign-in, and
              gated by the single-use oobCode in the emailed link. */}
          <Route path="/admin/forgot" element={<ForgotPassword />} />
          <Route path="/admin/reset" element={<ResetPassword />} />
          <Route element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/leads" element={<LeadsPage />} />
            <Route path="/admin/listings" element={<ListingsList />} />
            <Route path="/admin/listings/new" element={<ListingForm />} />
            <Route path="/admin/listings/:slug" element={<ListingForm />} />
            <Route path="/admin/testimonials" element={<TestimonialsList />} />
            <Route path="/admin/testimonials/new" element={<TestimonialForm />} />
            <Route path="/admin/testimonials/:id" element={<TestimonialForm />} />
            <Route path="/admin/awards" element={<AwardsList />} />
            <Route path="/admin/awards/new" element={<AwardForm />} />
            <Route path="/admin/awards/:id" element={<AwardForm />} />
            <Route path="/admin/blogs" element={<BlogList />} />
            <Route path="/admin/blogs/new" element={<BlogForm />} />
            <Route path="/admin/blogs/categories" element={<BlogCategories />} />
            <Route path="/admin/blogs/:slug" element={<BlogForm />} />
            <Route path="/admin/proven-results" element={<ProvenResultsList />} />
            <Route path="/admin/proven-results/new" element={<ProvenResultsForm />} />
            <Route path="/admin/proven-results/:id" element={<ProvenResultsForm />} />
            {/* Dedicated editors — redirect the old generic routes. */}
            <Route path="/admin/content/listings" element={<Navigate to="/admin/listings" replace />} />
            <Route path="/admin/content/testimonials" element={<Navigate to="/admin/testimonials" replace />} />
            <Route path="/admin/content/awards" element={<Navigate to="/admin/awards" replace />} />
            <Route path="/admin/content/blogs" element={<Navigate to="/admin/blogs" replace />} />
            <Route path="/admin/content/provenResults" element={<Navigate to="/admin/proven-results" replace />} />
            <Route path="/admin/content/:key" element={<CollectionManager />} />
            <Route path="/admin/seo" element={<SeoSettings />} />
            <Route path="/admin/tracking" element={<TrackingPage />} />
          </Route>

          {/* ---- Public site (shared Layout) ---- */}
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />

            {/* Why Me — parent overview removed; redirects to first sub-page */}
            <Route path="/why-me" element={<Navigate to="/why-me/about-me" replace />} />
            <Route path="/why-me/about-me" element={<AboutMePage />} />
            <Route path="/why-me/services" element={<ServicesPage />} />

            <Route path="/listings" element={<ListingsPage />} />
            <Route path="/listings/:slug" element={<ListingDetailPage />} />
            <Route path="/framework" element={<FrameworkPage />} />

            {/* Success Stories — parent overview removed */}
            <Route path="/success-stories" element={<Navigate to="/success-stories/proven-results" replace />} />
            <Route path="/success-stories/proven-results" element={<ProvenResultsPage />} />
            <Route path="/success-stories/proven-results/:id" element={<ProvenResultDetailPage />} />
            <Route path="/success-stories/testimonials" element={<TestimonialsPage />} />
            <Route path="/success-stories/awards" element={<AwardsPage />} />

            {/* Resources — parent overview removed */}
            <Route path="/resources" element={<Navigate to="/resources/property-valuation" replace />} />
            <Route path="/resources/property-valuation" element={<PropertyValuationPage />} />
            <Route path="/resources/calculators" element={<CalculatorsPage />} />
            <Route path="/resources/blogs" element={<BlogsPage />} />
            <Route path="/resources/blogs/:slug" element={<BlogDetailPage />} />

            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="*" element={<HomePage />} />
          </Route>
        </Routes>
        <Toaster position="bottom-left" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
